import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { CheckCircle, PenTool, Printer, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { printConsentRecord } from '../lib/printConsent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Reusable signature-capture dialog for a single resident.
const ConsentCaptureDialog = ({ resident, open, onClose, title = 'Capture Required Signatures', enforceRequired = false }) => {
  const [templates, setTemplates] = useState([]);
  const [records, setRecords] = useState([]);
  const [activeType, setActiveType] = useState(null);
  const [residentSig, setResidentSig] = useState('');
  const [staffSig, setStaffSig] = useState('');
  const [witnessSig, setWitnessSig] = useState('');
  const [checkedItems, setCheckedItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!resident) return;
    try {
      const [t, r] = await Promise.all([
        axios.get(`${API}/consent/templates`),
        axios.get(`${API}/consent-records?resident_id=${resident.id}`),
      ]);
      setTemplates(t.data);
      setRecords(r.data);
    } catch (e) { console.warn('consent templates/records fetch failed:', e?.response?.status); }
  }, [resident]);

  useEffect(() => { if (open) { load(); setActiveType(null); } }, [open, load]);

  const signedTypes = new Set(records.map((r) => r.document_type));
  const activeDoc = templates.find((t) => t.document_type === activeType);
  const requiredTypes = templates.filter((t) => t.required).map((t) => t.document_type);
  const unsignedRequired = requiredTypes.filter((dt) => !signedTypes.has(dt));
  const allRequiredSigned = unsignedRequired.length === 0;
  // When enforcing at intake, block closing until every required form is signed.
  const canClose = !enforceRequired || allRequiredSigned;

  const attemptClose = () => {
    if (canClose) onClose();
    else toast.error('All required consent forms must be signed at intake before continuing.');
  };

  const startSign = (docType) => {
    setActiveType(docType);
    setResidentSig(''); setStaffSig(''); setWitnessSig(''); setCheckedItems([]);
  };

  const submit = async () => {
    if (!residentSig.trim()) { toast.error('Resident signature required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/consent-records`, {
        resident_id: resident.id,
        document_type: activeType,
        resident_signature: residentSig,
        staff_signature: staffSig || null,
        witness_signature: witnessSig || null,
        items_received: activeDoc?.items ? checkedItems : null,
      });
      toast.success(`${activeDoc.title} signed`);
      setActiveType(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to record'); } finally { setBusy(false); }
  };

  const toggleItem = (item) => setCheckedItems((p) => p.includes(item) ? p.filter((x) => x !== item) : [...p, item]);
  const recordFor = (docType) => records.find((r) => r.document_type === docType);

  if (!resident) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) attemptClose(); }}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="consent-capture-dialog"
        hideClose={enforceRequired && !allRequiredSigned}
        onEscapeKeyDown={(e) => { if (!canClose) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (!canClose) e.preventDefault(); }}
        onInteractOutside={(e) => { if (!canClose) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PenTool className="h-5 w-5 text-indigo-600" /> {title}</DialogTitle>
          <DialogDescription>
            {resident.first_name} {resident.last_name} —{' '}
            {enforceRequired
              ? 'required consent forms must be signed at intake and cannot be completed later.'
              : 'sign each required document. Required forms are marked.'}
          </DialogDescription>
        </DialogHeader>

        {!activeType ? (
          <div className="space-y-2">
            {templates.map((t) => {
              const signed = signedTypes.has(t.document_type);
              return (
                <div key={t.document_type} className="flex items-center justify-between border rounded-md p-3" data-testid={`consent-item-${t.document_type}`}>
                  <div>
                    <div className="font-medium text-gray-900 flex items-center gap-2">
                      {t.title}
                      {t.required && <Badge className="bg-blue-100 text-blue-800 text-xs">required</Badge>}
                      {t.items && <Badge className="bg-purple-100 text-purple-800 text-xs">checklist</Badge>}
                    </div>
                  </div>
                  {signed ? (
                    <div className="flex items-center gap-2">
                      <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />signed</Badge>
                      <Button size="sm" variant="ghost" onClick={() => printConsentRecord(recordFor(t.document_type))} title="Print" data-testid={`print-${t.document_type}`}><Printer className="h-4 w-4" /></Button>
                    </div>
                  ) : (
                    <Button size="sm" onClick={() => startSign(t.document_type)} className="bg-indigo-600 hover:bg-indigo-700" data-testid={`sign-${t.document_type}`}>Sign <ChevronRight className="h-4 w-4 ml-1" /></Button>
                  )}
                </div>
              );
            })}
            <div className="flex items-center justify-between pt-2">
              {enforceRequired && !allRequiredSigned ? (
                <span className="text-sm text-red-600 font-medium" data-testid="consent-required-warning">
                  {unsignedRequired.length} required form{unsignedRequired.length > 1 ? 's' : ''} remaining — must be signed to continue.
                </span>
              ) : <span className="text-sm text-green-600 font-medium">{enforceRequired ? 'All required forms signed.' : ''}</span>}
              <Button
                variant={canClose ? 'outline' : 'ghost'}
                onClick={attemptClose}
                disabled={enforceRequired && !allRequiredSigned}
                data-testid="consent-capture-done"
              >
                Done
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-gray-50 border rounded-md p-4 text-sm text-gray-700" data-testid="capture-doc-body">
              <div className="font-semibold mb-1">{activeDoc.title}</div>{activeDoc.body}
            </div>
            {activeDoc.items && (
              <div className="space-y-2" data-testid="bedding-items">
                <div className="text-sm font-medium text-gray-700">Items issued at intake:</div>
                <div className="grid grid-cols-2 gap-2">
                  {activeDoc.items.map((item) => (
                    <label key={item} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={checkedItems.includes(item)} onCheckedChange={() => toggleItem(item)} data-testid={`bedding-item-${item.replace(/\s+/g, '-')}`} />
                      {item}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="grid md:grid-cols-3 gap-3">
              <div><label className="text-xs text-gray-500">Resident signature *</label><Input value={residentSig} onChange={(e) => setResidentSig(e.target.value)} data-testid="capture-resident-sig" /></div>
              <div><label className="text-xs text-gray-500">Staff signature</label><Input value={staffSig} onChange={(e) => setStaffSig(e.target.value)} data-testid="capture-staff-sig" /></div>
              <div><label className="text-xs text-gray-500">Witness</label><Input value={witnessSig} onChange={(e) => setWitnessSig(e.target.value)} /></div>
            </div>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setActiveType(null)}>Back</Button>
              <Button onClick={submit} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="capture-submit">Record signature</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ConsentCaptureDialog;
